/** @jsxImportSource @emotion/react */
import { Box, TextField, Button, IconButton, useTheme, Divider, Chip, Tooltip } from '@mui/material'
import { Add, Delete, CameraAlt, Map as MapIcon, Layers } from '@mui/icons-material'
import { Step, StepSection } from '@app/components/Risk/types'
import { formSectionStyles, formLabelStyles, formHelperTextStyles } from './StoryBuilder.styles'
import { SectionEditor } from './SectionEditor'
import { createEmptySection } from './types/localStory.types'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import { captureCompleteMapState, capturedStateToStoryMapState } from './utils/captureMapState'

interface StepEditorProps {
  step: Step
  stepIndex: number
  onUpdate: (updates: Partial<Step>) => void
  onDelete: () => void
  canDelete: boolean
}

export const StepEditor = ({ step, stepIndex, onUpdate, onDelete, canDelete }: StepEditorProps) => {
  const theme = useTheme()
  const { map, basemap } = useMap()
  const layers = useSelector((state: RootState) => state.riskMap.layers)

  const handleAddSection = () => {
    onUpdate({
      sections: [...step.sections, createEmptySection()],
    })
  }

  const handleUpdateSection = (sectionIndex: number, updates: Partial<StepSection>) => {
    const updatedSections = [...step.sections]
    updatedSections[sectionIndex] = {
      ...updatedSections[sectionIndex],
      ...updates,
    }
    onUpdate({ sections: updatedSections })
  }

  const handleDeleteSection = (sectionIndex: number) => {
    if (step.sections.length <= 1) return
    onUpdate({
      sections: step.sections.filter((_, i) => i !== sectionIndex),
    })
  }

  const handleCaptureMapState = () => {
    const captured = captureCompleteMapState(map, layers, basemap)
    if (!captured) return

    const mapState = capturedStateToStoryMapState(captured)
    onUpdate({
      location: mapState.location,
      basemap: mapState.basemap,
      elements: mapState.elements,
      scenarios: mapState.scenarios,
      contexts: mapState.contexts,
      regions: mapState.regions,
    })
  }

  const handleClearMapState = () => {
    onUpdate({
      location: undefined,
      basemap: undefined,
      elements: undefined,
      scenarios: undefined,
      contexts: undefined,
      regions: undefined,
    })
  }

  const hasMapState =
    step.location || step.elements?.length || step.scenarios?.length || step.contexts?.length

  return (
    <Box>
      <Box css={formSectionStyles(theme)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ fontWeight: 600, fontSize: '1rem' }}>Step {stepIndex + 1}</Box>
          {canDelete && (
            <IconButton size="small" onClick={onDelete} color="error">
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box css={formLabelStyles(theme)}>Step Title</Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter step title"
            value={step.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Box
              css={formLabelStyles(theme)}
              sx={{ mb: 0, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <MapIcon fontSize="small" />
              Map State
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Capture current map view and layers">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CameraAlt />}
                  onClick={handleCaptureMapState}
                >
                  Capture
                </Button>
              </Tooltip>
              {hasMapState && (
                <Button size="small" variant="text" color="error" onClick={handleClearMapState}>
                  Clear
                </Button>
              )}
            </Box>
          </Box>

          {hasMapState ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {step.location && (
                <Chip
                  size="small"
                  icon={<MapIcon />}
                  label={`${step.location.latitude.toFixed(4)}, ${step.location.longitude.toFixed(4)} (z${step.location.zoom?.toFixed(1)})`}
                  variant="outlined"
                />
              )}
              {step.basemap && <Chip size="small" label={step.basemap} variant="outlined" />}
              {step.elements && step.elements.length > 0 && (
                <Chip
                  size="small"
                  icon={<Layers />}
                  label={`${step.elements.length} assets`}
                  color="primary"
                  variant="outlined"
                />
              )}
              {step.scenarios && step.scenarios.length > 0 && (
                <Chip
                  size="small"
                  icon={<Layers />}
                  label={`${step.scenarios.length} hazards`}
                  color="warning"
                  variant="outlined"
                />
              )}
              {step.contexts && step.contexts.length > 0 && (
                <Chip
                  size="small"
                  icon={<Layers />}
                  label={`${step.contexts.length} info layers`}
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          ) : (
            <Box css={formHelperTextStyles(theme)}>
              No map state captured. Use the map and click "Capture" to save the current view.
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box css={formLabelStyles(theme)} sx={{ mb: 0 }}>
            Sections
          </Box>
          <Button size="small" startIcon={<Add />} onClick={handleAddSection}>
            Add Section
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {step.sections.map((section, sectionIndex) => (
            <SectionEditor
              key={sectionIndex}
              section={section}
              index={sectionIndex}
              onUpdate={(updates) => handleUpdateSection(sectionIndex, updates)}
              onDelete={() => handleDeleteSection(sectionIndex)}
              canDelete={step.sections.length > 1}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default StepEditor
